import React, { useEffect, useMemo, useState } from 'react';
import { Bot, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { businessCenterApi, chatApi, modelsApi } from '@/lib/api';

export const ChatModelPicker = ({ projectId, disabled = false }) => {
  const [models, setModels] = useState([]);
  const [modelId, setModelId] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [accountDefault, setAccountDefault] = useState('');
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    Promise.all([modelsApi.list(), chatApi.model(projectId), businessCenterApi.get(projectId)])
      .then(([catalog, preference, center]) => {
        if (!active) return;
        const enabled = catalog.models || [];
        setModels(enabled);
        setModelId(preference.model_id || catalog.default_id || enabled[0]?.id || '');
        setAccountDefault(center?.ai?.default_model_id || '');
      })
      .catch(() => { if (active) toast.error('Could not load chat models.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [projectId, reloadKey]);

  // 2026-06 — Model Roles saves a new chat role; refetch so the trigger
  // doesn't keep showing the old model until a page reload.
  useEffect(() => {
    const bump = () => setReloadKey((k) => k + 1);
    window.addEventListener('model-roles-changed', bump);
    return () => window.removeEventListener('model-roles-changed', bump);
  }, []);

  const families = useMemo(() => models.reduce((groups, model) => {
    const family = model.family || model.provider || 'Models';
    (groups[family] = groups[family] || []).push(model);
    return groups;
  }, {}), [models]);
  const selected = models.find((model) => model.id === modelId);
  const modelScope = modelId && modelId === accountDefault ? 'Account default' : 'Conversation override';

  const chooseModel = async (nextId) => {
    if (!nextId || nextId === modelId) return;
    setSaving(true);
    try {
      const preference = await chatApi.setModel(projectId, nextId);
      setModelId(preference.model_id);
      toast.success(`This conversation now uses ${models.find((m) => m.id === nextId)?.label || nextId}.`);
      try {
        const { trackChatModelSelected } = await import('@/lib/analytics');
        trackChatModelSelected(nextId);
      } catch { /* analytics must never block chat */ }
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Could not change the chat model.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="grid shrink-0 grid-cols-[auto,minmax(0,1fr)] items-center gap-2 border-b border-panel-border bg-panel-header/30 px-3 py-2"
      data-testid="chat-model-picker"
    >
      <div className="flex items-center gap-1.5 text-[13px] font-medium text-muted-foreground">
        {loading || saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Bot className="h-3.5 w-3.5 text-primary" />}
        <span>Reply with</span>
      </div>
      <Select value={modelId} onValueChange={chooseModel} disabled={disabled || loading || saving || !models.length}>
        <SelectTrigger
          className="h-8 min-w-0 bg-background px-2 text-[13.5px]"
          data-testid="chat-model-trigger"
          aria-label="Choose model for this conversation"
        >
          <SelectValue placeholder={loading ? 'Loading models…' : 'No models enabled'} />
        </SelectTrigger>
        <SelectContent data-testid="chat-model-menu">
          {Object.entries(families).map(([family, items]) => (
            <SelectGroup key={family}>
              <SelectLabel className="text-[13px] uppercase text-muted-foreground">{family}</SelectLabel>
              {items.map((model) => (
                <SelectItem
                  key={model.id}
                  value={model.id}
                  data-testid={`chat-model-option-${model.id}`}
                  title={model.recommended_for}
                >
                  {model.label} · {model.cost_tier}
                </SelectItem>
              ))}
            </SelectGroup>
          ))}
        </SelectContent>
      </Select>
      <span className="col-start-2 truncate text-[13px] text-muted-foreground" data-testid="chat-model-status">
        {selected ? `${modelScope} · ${selected.recommended_for || selected.label}` : (loading ? 'Loading Admin-enabled models…' : 'Choose an Admin-enabled model')}
      </span>
    </div>
  );
};